import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import CreditCardIcon from '@liquidweb-dev/undercurrent/icons/lucide/CreditCard';
import SettingsIcon from '@liquidweb-dev/undercurrent/icons/lucide/Settings';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Typography from '@liquidweb-dev/undercurrent/Typography';

import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import { Link as RouterLink } from 'react-router-dom';

import { IntroTarget, introTargets } from 'containers/introjs';
import useAuthenticated from 'modules/oidc/useAuthenticated';
import useAccountDetails from 'modules/queries/account/useDetails';
import useBillingBalance from 'modules/queries/billing/useBalance';
import { useNavDialogsContext } from '../navDialogsContext';

interface PastDueTooltipProps {
	isPastDue: boolean;
	pastDue: string;
	children: React.ReactElement;
}

const PastDueTooltip = ({
	isPastDue,
	pastDue,
	children,
}: PastDueTooltipProps) => {
	if (isPastDue) {
		return (
			<Tooltip
				variant="fancy"
				arrow
				title="Past due"
				message={
					<>
						A friendly reminder your payment of <strong>{pastDue}</strong> is
						due
					</>
				}
				placement="top"
			>
				{children}
			</Tooltip>
		);
	}

	return children;
};

export function LeftNavFooter(props: StackProps) {
	const isAuthed = useAuthenticated();
	const {
		data: billingBalanceData,
		isError: billingBalanceError,
		isLoading: isbillingBalanceLoading,
	} = useBillingBalance({
		enabled: isAuthed,
	});
	const {
		data: accountUserDetailsData,
		isError: accountUserDetailsError,
		isLoading: isAccountUserDetailsLoading,
	} = useAccountDetails({}, { enabled: isAuthed, retry: true });

	const { setIsMakePaymentOpen } = useNavDialogsContext();

	const accountNumber = accountUserDetailsData?.accnt;
	const balanceDue = formatCurrency(billingBalanceData?.accountBalanceDisplay);
	const pastDue = formatCurrency(billingBalanceData?.pastDue);
	const isPastDue = (billingBalanceData?.pastDue || 0) > 0;

	if (billingBalanceError && accountUserDetailsError) {
		// likely means the user does not have permission to view this data - so
		// just don't show anything
		return null;
	}

	return (
		<>
			<IntroTarget introId={introTargets.navAccountBalance}>
				<Stack flexDirection="column" {...props}>
					{!billingBalanceError && (
						<Stack
							direction="row"
							justifyContent="stretch"
							alignItems="center"
							paddingTop={100}
							paddingBottom={200}
							paddingX={150}
							borderRadius={(theme) =>
								`${theme.borderRadius.roundedSm} ${theme.borderRadius.roundedSm} 0 0`
							}
							marginBottom={-100}
							gap={100}
							useFlexGap
							bgcolor={(theme) =>
								!isPastDue
									? theme.vars.palette.uc.bg.medium
									: theme.vars.palette.uc.danger.bg.main
							}
						>
							<PastDueTooltip pastDue={pastDue} isPastDue={isPastDue}>
								<Stack
									direction="row"
									flex="100% 1 1"
									alignItems="center"
									gap={50}
									useFlexGap
								>
									<Stack direction="row" gap={50}>
										<Typography variant="label-md">
											Total due{' '}
											<Typography
												component="span"
												variant="label-md"
												sx={{
													fontWeight: 500,
													color: ({ vars }) =>
														isPastDue
															? vars.palette.uc.danger.text.main
															: vars.palette.uc.text.main,
												}}
											>
												{balanceDue}
											</Typography>
										</Typography>
										{isbillingBalanceLoading && (
											<Skeleton
												variant="rounded"
												height={12}
												width={50}
												sx={({ vars }) => ({
													backgroundColor: vars.palette.uc.bg.weak,
													paddingLeft: 50,
												})}
											/>
										)}
									</Stack>
								</Stack>
							</PastDueTooltip>
							<Tooltip title="Make a payment" placement="right">
								<IconButton
									variant="ghost"
									size="small"
									onClick={() => setIsMakePaymentOpen(true)}
									color={isPastDue ? 'danger' : 'default'}
								>
									<CreditCardIcon />
								</IconButton>
							</Tooltip>
						</Stack>
					)}
					{!accountUserDetailsError && (
						<Stack
							sx={{
								flexDirection: 'row',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: 100,
								paddingX: 150,
								paddingY: 100,
								border: '1px solid',
								borderColor: ({ vars }) => vars.palette.uc.border.main,
								borderRadius: ({ borderRadius }) => borderRadius.roundedSm,
								backgroundColor: ({ vars }) => vars.palette.uc.bg.weak,
							}}
						>
							<Stack
								flexDirection="row"
								justifyContent="stretch"
								alignItems="center"
								gap={100}
							>
								<Typography variant="label-md">
									Account # {accountNumber}
								</Typography>
								{isAccountUserDetailsLoading && (
									<Skeleton variant="rounded" height={12} width={50} />
								)}
							</Stack>

							<Tooltip title="Account settings" placement="right">
								<IconButton
									variant="ghost"
									size="small"
									component={RouterLink}
									to="/account"
								>
									<SettingsIcon />
								</IconButton>
							</Tooltip>
						</Stack>
					)}
				</Stack>
			</IntroTarget>
		</>
	);
}

export default LeftNavFooter;
